import { PublicLayout } from "@/components/PublicLayout";
import { Button } from "@/components/ui/button";
import { ScrollReveal } from "@/components/ScrollReveal";
import {
  ArrowRight, Check, X, Sparkles, Building2, Users, Crown,
  GraduationCap, Globe, Rocket, Shield, Star, ChevronRight,
  MapPin, Zap, BarChart3, Calendar, Phone, Mail, Loader2,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

/* ─── Entreprises Plans ─── */
const entreprisePlans = [
  {
    name: "Starter",
    package_id: "starter",
    target: "Entrepreneurs solo & Start-ups",
    price: "1 500",
    period: "/mois HT",
    features: [
      "Business plan complet + étude de faisabilité",
      "Plan financier prévisionnel 3 ans",
      "Analyse & solutions de financement",
      "Choix juridique & immatriculation",
      "Ouverture compte bancaire pro",
      "Création site Internet complet",
      "Tous canaux de communication",
      "CRM & outils comptables",
      "Rapports matin & soir quotidiens",
      "Accompagnement 5 ans",
    ],
    notIncluded: [
      "Analyse d'entreprise existante",
      "Attribution poste IA dans l'organisation",
      "Session stratégique avec Joël",
    ],
    cta: "Démarrer avec Starter",
    icon: Rocket,
  },
  {
    name: "Medium",
    package_id: "medium",
    target: "PME · 10-50 salariés",
    price: "2 000",
    period: "/mois HT",
    popular: true,
    features: [
      "Tout Starter inclus",
      "Analyse d'entreprise existante",
      "Propositions d'amélioration IA",
      "Attribution d'un poste IA dans votre organisation",
      "Dashboard avancé multi-équipe",
      "Automatisation prospection & relances",
      "Reporting financier détaillé",
      "Support prioritaire 6j/7",
      "Accompagnement 5 ans",
    ],
    notIncluded: [
      "Session stratégique mensuelle avec Joël",
      "Accès réseau investisseurs",
    ],
    cta: "Choisir Medium",
    icon: Users,
  },
  {
    name: "Premium",
    package_id: "premium",
    target: "ETI & Grands comptes · +50 salariés",
    price: "3 000",
    period: "/mois HT",
    features: [
      "Tout Medium inclus",
      "Session stratégique mensuelle avec Joël",
      "Accès réseau investisseurs Grafton",
      "IA dédiée à votre organisation",
      "API & intégrations sur mesure",
      "Account manager dédié",
      "Dashboard direction générale",
      "Audit sécurité & conformité",
      "Support dédié 7j/7",
      "Accompagnement illimité",
    ],
    notIncluded: [],
    cta: "Contacter Premium",
    icon: Crown,
  },
];

/* ─── Agences Immobilières Plans ─── */
const agencePlans = [
  {
    name: "Starter",
    package_id: "agence_starter",
    target: "Agent indépendant · 1 agent",
    price: "149",
    period: "/mois",
    annualPrice: "125",
    features: [
      "Alertes leads en temps réel sur 1 arrondissement",
      "Carte de matching interactive",
      "Fiche profil agence sur Grafton IA",
      "5 mises en relation / mois",
      "Support email 5j/7",
    ],
    cta: "Démarrer",
    icon: MapPin,
  },
  {
    name: "Business",
    package_id: "agence_business",
    target: "Agence · 1 à 5 agents",
    price: "390",
    period: "/mois",
    annualPrice: "325",
    popular: true,
    features: [
      "Alertes leads sur 3 arrondissements",
      "Exclusivité géographique sur votre secteur",
      "Tableau de bord agence — suivi leads",
      "20 mises en relation / mois",
      "Badge \"Agence Partenaire Grafton IA\"",
      "Statistiques marché temps réel",
      "1 session déploiement QR codes",
      "Support prioritaire 6j/7",
    ],
    cta: "Devenir partenaire",
    icon: Building2,
  },
  {
    name: "Premium",
    package_id: "agence_premium",
    target: "Réseau · 5 agents et plus",
    price: "890",
    period: "/mois",
    annualPrice: "742",
    features: [
      "Alertes leads Paris entier ou zone dédiée",
      "Exclusivité multi-arrondissements (jusqu'à 5)",
      "Tableau de bord réseau — vue par agent",
      "Leads illimités",
      "API connexion CRM existant",
      "Co-branding \"Powered by Grafton IA\"",
      "Account manager dédié",
      "Déploiement QR codes inclus",
      "Accès anticipé futures fonctionnalités",
    ],
    cta: "Nous contacter",
    icon: Globe,
  },
];

/* ─── Séminaire Plans ─── */
const seminairePlans = [
  {
    name: "Founder",
    target: "Entrepreneurs en lancement",
    price: "4 900",
    period: "€ HT",
    features: [
      "Programme complet 5 jours",
      "Hébergement & restauration Club Med",
      "Sessions Grafton IA collectives",
      "1 session individuelle 45 min",
      "Accès communauté Grafton 1 an",
      "Dîner dirigeants J4",
    ],
    icon: Rocket,
    cta: "Réserver Founder",
  },
  {
    name: "Executive",
    target: "Dirigeants PME & ETI",
    price: "9 900",
    period: "€ HT",
    popular: true,
    features: [
      "Programme complet 5 jours",
      "Hébergement & restauration Club Med",
      "Sessions collectives & privées",
      "3 sessions individuelles 1h avec Joël",
      "Roadmap Grafton IA personnalisée",
      "Communauté Grafton à vie",
      "Mise en relation investisseurs",
    ],
    icon: Star,
    cta: "Réserver Executive",
  },
  {
    name: "Investor",
    target: "Grands comptes & Investisseurs",
    price: "19 000",
    period: "€ HT",
    features: [
      "Programme complet 5 jours",
      "Suite privée Club Med",
      "Accompagnement Grafton 6 mois post",
      "Accès illimité à Joël Broulou",
      "Opportunité co-investissement",
      "Table ronde privée dirigeants J4",
      "Communauté + réseau investisseurs à vie",
    ],
    icon: Crown,
    cta: "Réserver Investor",
  },
];

/* ─── Agences comparison table ─── */
const agenceComparison = [
  { feature: "Alertes leads temps réel", starter: true, business: true, premium: true },
  { feature: "Arrondissements couverts", starter: "1", business: "3", premium: "Illimité" },
  { feature: "Mises en relation / mois", starter: "5", business: "20", premium: "Illimité" },
  { feature: "Exclusivité géographique", starter: false, business: true, premium: true },
  { feature: "Carte de matching", starter: true, business: true, premium: true },
  { feature: "Tableau de bord agence", starter: false, business: true, premium: true },
  { feature: "Badge Partenaire", starter: false, business: true, premium: true },
  { feature: "Déploiement QR codes", starter: false, business: "1 session", premium: "Inclus" },
  { feature: "Connexion CRM", starter: false, business: false, premium: true },
  { feature: "Account manager dédié", starter: false, business: false, premium: true },
];

/* ─── Tab type ─── */
type TabId = "entreprises" | "agences" | "ateliers" | "seminaires";

const tabs: { id: TabId; label: string; icon: typeof Rocket }[] = [
  { id: "entreprises", label: "Entreprises", icon: Rocket },
  { id: "agences", label: "Agences Immobilières", icon: Building2 },
  { id: "ateliers", label: "Ateliers", icon: GraduationCap },
  { id: "seminaires", label: "Séminaires", icon: Globe },
];

/* ─── Plan Card component ─── */
const PlanCard = ({ plan, showAnnual = false }: { plan: any; showAnnual?: boolean }) => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleSubscribe = async () => {
    // If no package_id (e.g. seminar, enterprise contact), fallback to /auth
    if (!plan.package_id) {
      navigate("/auth");
      return;
    }
    if (!user) {
      toast("Créez votre compte pour souscrire à un plan", {
        description: "Redirection vers l'inscription...",
      });
      // Store target plan so user comes back here after signup
      try {
        sessionStorage.setItem("pending_subscription", plan.package_id);
      } catch {
        /* ignore */
      }
      setTimeout(() => navigate("/auth"), 1200);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/subscriptions/checkout`, {
        method: "POST",
        credentials: "include" as RequestCredentials,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          package_id: plan.package_id,
          origin_url: window.location.origin,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Impossible de démarrer le paiement");
        return;
      }
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error("URL Stripe manquante");
      }
    } catch {
      toast.error("Erreur de connexion à Stripe");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`relative rounded-2xl border p-8 flex flex-col h-full transition-all duration-300 hover:shadow-[0_8px_40px_rgba(0,0,0,0.2)] ${
        plan.popular
          ? "border-primary/40 bg-card shadow-[0_8px_32px_hsl(var(--glow-amber)/0.08)]"
          : "border-border/40 bg-card/50 hover:border-border/60"
      }`}
      data-testid={`plan-card-${plan.name.toLowerCase()}`}
    >
      {plan.popular && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap">
          Recommandé
        </span>
      )}

      <div className="flex items-center gap-3 mb-4">
        <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${plan.popular ? "bg-primary/15" : "bg-secondary/50"}`}>
          <plan.icon className={`h-5 w-5 ${plan.popular ? "text-primary" : "text-muted-foreground"}`} />
        </div>
        <div>
          <h3 className="text-lg font-bold">{plan.name}</h3>
          <p className="text-xs text-muted-foreground">{plan.target}</p>
        </div>
      </div>

      <div className="mb-6">
        <div className="flex items-baseline gap-1">
          <span className="text-4xl font-bold tracking-tight">
            {showAnnual && plan.annualPrice ? plan.annualPrice : plan.price}€
          </span>
          <span className="text-muted-foreground text-sm">{plan.period}</span>
        </div>
        {showAnnual && plan.annualPrice && (
          <p className="text-xs text-primary mt-1 font-medium">2 mois offerts sur l'annuel</p>
        )}
      </div>

      <ul className="space-y-3 mb-8 flex-1">
        {plan.features.map((f: string) => (
          <li key={f} className="flex items-start gap-2.5 text-sm text-muted-foreground">
            <Check className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
            <span>{f}</span>
          </li>
        ))}
        {plan.notIncluded?.map((f: string) => (
          <li key={f} className="flex items-start gap-2.5 text-sm text-muted-foreground/40">
            <X className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span className="line-through">{f}</span>
          </li>
        ))}
      </ul>

      <Button
        variant={plan.popular ? "hero" : "hero-outline"}
        className="w-full h-12"
        onClick={handleSubscribe}
        disabled={loading}
        data-testid={`plan-cta-${plan.name.toLowerCase()}`}
      >
        {loading ? (
          <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Redirection…</>
        ) : (
          <>{plan.cta} <ArrowRight className="ml-1.5 h-4 w-4" /></>
        )}
      </Button>
    </div>
  );
};

/* ─── Page ─── */
const TarifsPage = () => {
  const [activeTab, setActiveTab] = useState<TabId>("entreprises");
  const [annualBilling, setAnnualBilling] = useState(false);

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="py-20 md:py-28">
        <div className="container">
          <ScrollReveal>
            <div className="text-center max-w-3xl mx-auto">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-semibold mb-6">
                <Sparkles className="h-3.5 w-3.5" /> Tarifs transparents
              </div>
              <h1 data-testid="tarifs-title" className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight mb-6">
                Investissez dans <span className="text-gradient">l'exécution</span>
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed max-w-xl mx-auto">
                Grafton IA n'est pas un logiciel. C'est votre associé qui exécute 24h/24.
                Choisissez l'offre adaptée à votre ambition.
              </p>
            </div>
          </ScrollReveal>

          {/* Tabs */}
          <ScrollReveal delay={100}>
            <div className="flex justify-center mt-12 mb-16">
              <div className="inline-flex bg-secondary/30 border border-border/40 rounded-2xl p-1.5 gap-1 flex-wrap justify-center">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    data-testid={`tab-${tab.id}`}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${
                      activeTab === tab.id
                        ? "bg-primary text-primary-foreground shadow-[0_2px_12px_hsl(var(--glow-amber)/0.3)]"
                        : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                    }`}
                  >
                    <tab.icon className="h-4 w-4" />
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
          </ScrollReveal>

          {/* ═══ ENTREPRISES ═══ */}
          {activeTab === "entreprises" && (
            <div>
              <ScrollReveal>
                <div className="text-center mb-12">
                  <h2 className="text-2xl md:text-3xl font-bold mb-3">
                    Offres <span className="text-gradient">Entreprises</span>
                  </h2>
                  <p className="text-muted-foreground max-w-lg mx-auto">
                    De la création à la gestion quotidienne, Grafton IA exécute à votre place pendant 5 ans.
                  </p>
                </div>
              </ScrollReveal>

              <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
                {entreprisePlans.map((plan, i) => (
                  <ScrollReveal key={plan.name} delay={i * 100}>
                    <PlanCard plan={plan} />
                  </ScrollReveal>
                ))}
              </div>

              {/* Value prop */}
              <ScrollReveal delay={400}>
                <div className="mt-16 rounded-2xl border border-primary/20 bg-primary/5 p-8 max-w-4xl mx-auto">
                  <div className="flex flex-col md:flex-row items-center gap-8">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold mb-3">Accompagnement 5 ans inclus</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        Grafton IA ne s'arrête pas au lancement. Il vous accompagne dans l'atteinte
                        de vos objectifs court, moyen et long termes — en optimisant chaque action,
                        chaque décision, chaque étape de votre croissance.
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-5xl font-bold text-primary">x9,4</p>
                      <p className="text-sm text-muted-foreground mt-1">ROI moyen</p>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            </div>
          )}

          {/* ═══ AGENCES IMMOBILIÈRES ═══ */}
          {activeTab === "agences" && (
            <div>
              <ScrollReveal>
                <div className="text-center mb-8">
                  <h2 className="text-2xl md:text-3xl font-bold mb-3">
                    Partenaires <span className="text-gradient">Agences Immobilières</span>
                  </h2>
                  <p className="text-muted-foreground max-w-lg mx-auto">
                    Transformez votre agence en machine à transactions. Des leads qualifiés en temps réel.
                  </p>
                </div>
              </ScrollReveal>

              {/* Stats bar */}
              <ScrollReveal delay={50}>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto mb-12">
                  {[
                    { value: "-70%", label: "temps en prospection" },
                    { value: "+2", label: "transactions/agent/an" },
                    { value: "x8", label: "retour sur investissement" },
                    { value: "30j", label: "premiers leads" },
                  ].map((s) => (
                    <div key={s.label} className="text-center p-4 rounded-xl bg-secondary/30 border border-border/30">
                      <p className="text-2xl font-bold text-primary">{s.value}</p>
                      <p className="text-xs text-muted-foreground mt-1">{s.label}</p>
                    </div>
                  ))}
                </div>
              </ScrollReveal>

              {/* Billing toggle */}
              <ScrollReveal delay={100}>
                <div className="flex justify-center mb-10">
                  <div className="inline-flex items-center gap-3 bg-secondary/30 border border-border/40 rounded-full px-4 py-2">
                    <button
                      onClick={() => setAnnualBilling(false)}
                      className={`text-sm font-medium px-3 py-1 rounded-full transition-all ${!annualBilling ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
                    >
                      Mensuel
                    </button>
                    <button
                      onClick={() => setAnnualBilling(true)}
                      className={`text-sm font-medium px-3 py-1 rounded-full transition-all ${annualBilling ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
                    >
                      Annuel <span className="text-xs opacity-70">-2 mois</span>
                    </button>
                  </div>
                </div>
              </ScrollReveal>

              <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
                {agencePlans.map((plan, i) => (
                  <ScrollReveal key={plan.name} delay={i * 100}>
                    <PlanCard plan={plan} showAnnual={annualBilling} />
                  </ScrollReveal>
                ))}
              </div>

              {/* Comparison table */}
              <ScrollReveal delay={400}>
                <div className="mt-16 max-w-4xl mx-auto">
                  <h3 className="text-xl font-bold text-center mb-8">Tout comparer en un coup d'oeil</h3>
                  <div className="rounded-xl border border-border/40 overflow-hidden">
                    <table className="w-full text-sm" data-testid="agence-comparison-table">
                      <thead>
                        <tr className="border-b border-border/40 bg-secondary/20">
                          <th className="text-left p-4 font-semibold">Fonctionnalité</th>
                          <th className="text-center p-4 font-semibold">Starter</th>
                          <th className="text-center p-4 font-semibold text-primary">Business</th>
                          <th className="text-center p-4 font-semibold">Premium</th>
                        </tr>
                      </thead>
                      <tbody>
                        {agenceComparison.map((row, i) => (
                          <tr key={row.feature} className={i % 2 === 0 ? "bg-card/30" : ""}>
                            <td className="p-4 text-muted-foreground">{row.feature}</td>
                            {["starter", "business", "premium"].map((plan) => {
                              const val = (row as any)[plan];
                              return (
                                <td key={plan} className="text-center p-4">
                                  {val === true ? (
                                    <Check className="h-4 w-4 text-primary mx-auto" />
                                  ) : val === false ? (
                                    <span className="text-muted-foreground/30">—</span>
                                  ) : (
                                    <span className="text-muted-foreground">{val}</span>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </ScrollReveal>
            </div>
          )}

          {/* ═══ ATELIERS ═══ */}
          {activeTab === "ateliers" && (
            <div>
              <ScrollReveal>
                <div className="text-center mb-12">
                  <h2 className="text-2xl md:text-3xl font-bold mb-3">
                    Atelier <span className="text-gradient">L'entreprise augmentée</span>
                  </h2>
                  <p className="text-muted-foreground max-w-lg mx-auto">
                    2 heures pour comprendre comment Grafton IA exécute à votre place.
                    Présenté par Joël Broulou, fondateur.
                  </p>
                </div>
              </ScrollReveal>

              <div className="max-w-4xl mx-auto">
                {/* Atelier card */}
                <ScrollReveal delay={100}>
                  <div className="rounded-2xl border border-primary/30 bg-card p-10 shadow-[0_8px_40px_hsl(var(--glow-amber)/0.06)]">
                    <div className="grid md:grid-cols-2 gap-10">
                      <div>
                        <div className="flex items-center gap-2 mb-6">
                          <div className="h-12 w-12 rounded-xl bg-primary/15 flex items-center justify-center">
                            <GraduationCap className="h-6 w-6 text-primary" />
                          </div>
                          <div>
                            <h3 className="text-2xl font-bold">120€</h3>
                            <p className="text-xs text-muted-foreground">par personne · toutes taxes incluses</p>
                          </div>
                        </div>

                        <div className="inline-block px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-semibold mb-6">
                          Déductible de votre 1ère mensualité Grafton IA
                        </div>

                        <ul className="space-y-3">
                          {[
                            "Atelier 2h avec Joël Broulou, fondateur",
                            "Démo live agent conversationnel Grafton IA",
                            "Entretien individuel 20 min offert après",
                            "Accès au dossier investisseur Grafton IA",
                            "Groupe limité à 20 personnes maximum",
                            "Lieu premium Paris",
                          ].map((f) => (
                            <li key={f} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                              <Check className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" /> {f}
                            </li>
                          ))}
                        </ul>

                        <Link to="/auth" className="block mt-8">
                          <Button variant="hero" size="lg" className="w-full h-12" data-testid="atelier-cta">
                            Réserver ma place — 120€ <ArrowRight className="ml-1.5 h-4 w-4" />
                          </Button>
                        </Link>
                      </div>

                      {/* Programme */}
                      <div>
                        <h4 className="text-sm font-semibold uppercase tracking-wider text-primary mb-5">Programme</h4>
                        <div className="space-y-4">
                          {[
                            { time: "0:00 — 0:20", title: "Le parcours du fondateur", desc: "Joël Broulou présente son parcours — succès, échecs, leçons." },
                            { time: "0:20 — 0:35", title: "Tour de table", desc: "Chaque participant se présente. Secteur, projet, défi principal." },
                            { time: "0:35 — 1:00", title: "Présentation Grafton IA", desc: "Démo live. Les 5 cerveaux IA. La différence entre conseiller et exécuter." },
                            { time: "1:00 — 1:25", title: "V1 et levée de fonds", desc: "La roadmap. La stratégie. Les opportunités d'investissement." },
                            { time: "1:25 — 1:45", title: "L'entreprise augmentée", desc: "La vision profonde derrière Grafton IA." },
                            { time: "1:45 — 2:00", title: "Questions & échanges", desc: "Discussion libre. Entretien individuel offert." },
                          ].map((step, i) => (
                            <div key={i} className="flex gap-3">
                              <div className="text-[10px] font-mono text-muted-foreground/50 w-24 shrink-0 pt-0.5">{step.time}</div>
                              <div>
                                <p className="text-sm font-medium">{step.title}</p>
                                <p className="text-xs text-muted-foreground mt-0.5">{step.desc}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </ScrollReveal>

                {/* Grid stats */}
                <ScrollReveal delay={200}>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10">
                    {[
                      { value: "120€", label: "Par personne" },
                      { value: "20", label: "Places max" },
                      { value: "2h", label: "Format intensif" },
                      { value: "x9,4", label: "ROI moyen Grafton" },
                    ].map((s) => (
                      <div key={s.label} className="text-center p-4 rounded-xl bg-secondary/30 border border-border/30">
                        <p className="text-2xl font-bold text-primary">{s.value}</p>
                        <p className="text-xs text-muted-foreground mt-1">{s.label}</p>
                      </div>
                    ))}
                  </div>
                </ScrollReveal>
              </div>
            </div>
          )}

          {/* ═══ SÉMINAIRES ═══ */}
          {activeTab === "seminaires" && (
            <div>
              <ScrollReveal>
                <div className="text-center mb-12">
                  <h2 className="text-2xl md:text-3xl font-bold mb-3">
                    Séminaire <span className="text-gradient">Martinique</span>
                  </h2>
                  <p className="text-muted-foreground max-w-lg mx-auto">
                    5 jours immersifs au Club Med Martinique pour transformer votre vision stratégique
                    et maîtriser l'intelligence artificielle.
                  </p>
                </div>
              </ScrollReveal>

              {/* Stats */}
              <ScrollReveal delay={50}>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto mb-12">
                  {[
                    { value: "5", label: "Jours immersifs" },
                    { value: "15", label: "Places max" },
                    { value: "94%", label: "changent de cap" },
                    { value: "3x", label: "accélération post" },
                  ].map((s) => (
                    <div key={s.label} className="text-center p-4 rounded-xl bg-secondary/30 border border-border/30">
                      <p className="text-2xl font-bold text-primary">{s.value}</p>
                      <p className="text-xs text-muted-foreground mt-1">{s.label}</p>
                    </div>
                  ))}
                </div>
              </ScrollReveal>

              <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
                {seminairePlans.map((plan, i) => (
                  <ScrollReveal key={plan.name} delay={i * 100}>
                    <PlanCard plan={plan} />
                  </ScrollReveal>
                ))}
              </div>

              {/* Programme */}
              <ScrollReveal delay={400}>
                <div className="mt-16 max-w-4xl mx-auto">
                  <h3 className="text-xl font-bold text-center mb-8">5 jours pour transformer votre trajectoire</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
                    {[
                      { day: "J1", title: "Vision & IA", items: ["Conférence 2030", "Mastermind", "Diagnostic Grafton"] },
                      { day: "J2", title: "Exécution", items: ["Workshop performance", "Cas concrets IA", "Plan individualisé"] },
                      { day: "J3", title: "Régénération", items: ["Charge mentale", "Activité nature", "Networking"] },
                      { day: "J4", title: "Stratégie", items: ["Roadmap personnalisée", "Dîner investisseurs", "Investissement croisé"] },
                      { day: "J5", title: "Communauté", items: ["Plan 2025-2030", "Communauté à vie", "Engagement collectif"] },
                    ].map((d) => (
                      <div key={d.day} className="rounded-xl border border-border/40 bg-card/50 p-5">
                        <div className="text-xs font-bold text-primary mb-1">{d.day}</div>
                        <h4 className="text-sm font-semibold mb-3">{d.title}</h4>
                        <ul className="space-y-1.5">
                          {d.items.map((item) => (
                            <li key={item} className="text-xs text-muted-foreground flex items-start gap-1.5">
                              <ChevronRight className="h-3 w-3 text-primary shrink-0 mt-0.5" /> {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              </ScrollReveal>
            </div>
          )}
        </div>
      </section>

      {/* ═══ FAQ ═══ */}
      <section className="py-16 border-t border-border/30">
        <div className="container max-w-3xl">
          <ScrollReveal>
            <h2 className="text-2xl font-bold text-center mb-10">Questions fréquentes</h2>
          </ScrollReveal>
          <div className="space-y-4">
            {[
              { q: "Y a-t-il un engagement minimum ?", a: "Le plan mensuel est sans engagement — résiliable à tout moment avec 30 jours de préavis. Nous sommes confiants dans la valeur créée." },
              { q: "Comment fonctionne l'exclusivité géographique (Agences) ?", a: "À partir du plan Business, votre agence bénéficie d'une exclusivité sur votre arrondissement — aucune autre agence concurrente sur ce secteur. Premier arrivé, premier servi." },
              { q: "Peut-on changer de plan en cours d'abonnement ?", a: "Oui — upgrade à tout moment. La différence est calculée au prorata. Le downgrade prend effet au prochain cycle de facturation." },
              { q: "Les 120€ de l'atelier sont-ils déductibles ?", a: "Oui, les 120€ sont intégralement déduits de votre première mensualité Grafton IA si vous souscrivez dans les 30 jours suivant l'atelier." },
              { q: "Grafton IA remplace-t-il mes employés ?", a: "Non. Grafton IA amplifie la performance humaine, il ne remplace jamais l'humain. Il exécute les tâches répétitives et chronophages pour que vos équipes se concentrent sur la stratégie." },
            ].map((faq, i) => (
              <ScrollReveal key={i} delay={i * 50}>
                <details className="group rounded-xl border border-border/40 bg-card/30 overflow-hidden" data-testid={`faq-${i}`}>
                  <summary className="flex items-center justify-between p-5 cursor-pointer text-sm font-semibold hover:text-primary transition-colors">
                    {faq.q}
                    <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform group-open:rotate-90" />
                  </summary>
                  <div className="px-5 pb-5 text-sm text-muted-foreground leading-relaxed">{faq.a}</div>
                </details>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ CTA ═══ */}
      <section className="py-20 border-t border-border/30">
        <div className="container">
          <ScrollReveal>
            <div className="relative rounded-2xl overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/15 via-card to-primary/5" />
              <div className="absolute inset-0 border border-primary/15 rounded-2xl" />
              <div className="relative px-8 py-16 text-center">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Prêt à passer à <span className="text-gradient">l'exécution</span> ?
                </h2>
                <p className="text-muted-foreground mb-8 max-w-md mx-auto">
                  Rejoignez les dirigeants qui ont choisi de déléguer l'exécution à Grafton IA.
                </p>
                <div className="flex flex-col sm:flex-row justify-center gap-4">
                  <Link to="/auth">
                    <Button variant="hero" size="lg" className="h-14 px-10" data-testid="tarifs-final-cta">
                      Commencer maintenant <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </Link>
                </div>
                <p className="text-xs text-muted-foreground mt-4">
                  Sans carte bancaire · Annulation libre · Support réactif
                </p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </PublicLayout>
  );
};

export default TarifsPage;
